import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router';

import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import {
  useAppDispatch,
  useMedia,
  useMobileSimplifiedToothChartVisibility,
} from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { MobileLayout } from '@/shared/ui';

import {
  ReportLayout,
  ReportSettingsToggle,
  i18n,
  reportsModel,
} from '@/entities/reports';

import { MedicalImageToolbar } from '@/features/medicalImageToolbar';
import { MaskFiltersWrapper, MaskTooltip } from '@/features/renderMasks';
import { useReportCheck } from '@/features/reportCheck';
import { SimplifiedToothChart } from '@/features/simplifiedToothChart';

import { Conclusion } from '@/widgets/Conclusion';
import { MobileHeader } from '@/widgets/Header';
import { IOXRayReportRender } from '@/widgets/IOXRayReportRender';
import { Report2DToothChart } from '@/widgets/Report2DToothChart';
import { ReportActions } from '@/widgets/ReportActions';
import { ToothCardList } from '@/widgets/ToothCard';

import { ToothNumberEditPopup } from '@/pages/GpReport/ui/ToothNumberEditPopup';

import { IOXRayChangeTeethNumberModal } from '../IOXRayChangeTeethNumberModal/IOXRayChangeTeethNumberModal';
import { IOXRayImageSlider } from '../IOXrayImageSlider/IOXRayImageSlider';

import styles from './IOXRayReport.module.scss';

export const IOXRayReport = () => {
  const { patientID, reportID = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const dispatch = useAppDispatch();

  const widgetRef = useRef<HTMLDivElement>(null);

  const mobileToothChartStyle = styles.mobileToothChart;

  const { simplifiedChartRef, shouldShowMobileSimplifiedToothChart } =
    useMobileSimplifiedToothChartVisibility({
      mobileToothChartStyle,
    });

  useReportCheck(reportID);

  useEffect(() => {
    trackEvent({
      eventName: 'IOXRayReport Page Viewed',
      properties: {
        patient_id: patientID,
        report_id: reportID,
        report_type: ReportType.ReportType_IOXRay_GP,
      },
      isPageViewEvent: true,
    });
  }, []);

  useEffect(() => {
    // Get activeItemID from URL and set focusedMetaImageID
    const activeItemID = searchParams.get('activeItemID');
    if (activeItemID) {
      dispatch(reportsModel.actions.setFocusedMetaImageID(activeItemID));
    }
  }, [searchParams]);

  if (isMobile) {
    return (
      <MobileLayout>
        <MobileHeader />

        {shouldShowMobileSimplifiedToothChart && (
          <div
            ref={simplifiedChartRef}
            className={styles.simplifiedToothChartWrapper}
          >
            <SimplifiedToothChart
              reportID={reportID}
              patientID={patientID ?? ''}
              useScroll
              mergePrimaryWithAdult
            />
          </div>
        )}
        <MobileLayout.Main>
          <IOXRayReportRender>
            <MedicalImageToolbar
              toolbarID="main"
              className={styles.mobileToolbar}
            />
            <MaskFiltersWrapper className={styles.maskFilters} />
            <MaskTooltip />
            <IOXRayImageSlider />
          </IOXRayReportRender>

          <ReportSettingsToggle />

          <Report2DToothChart className={mobileToothChartStyle} />
          <ToothCardList />
          <Conclusion />
        </MobileLayout.Main>
        <MobileLayout.Footer>
          <ReportActions
            patientID={patientID as string}
            reportID={reportID as string}
          />
        </MobileLayout.Footer>

        {/* Modals */}
        <IOXRayChangeTeethNumberModal />
        <ToothNumberEditPopup />
      </MobileLayout>
    );
  }

  return (
    <>
      <ReportLayout.LeftColumn data-section="ioxray-report">
        <h1>{formatMessage(i18n[ReportType.ReportType_IOXRay_GP])}</h1>
        <div ref={widgetRef} style={{ height: '100%' }}>
          <IOXRayReportRender>
            <ToothNumberEditPopup />

            <MedicalImageToolbar toolbarID="main" />
            <MaskFiltersWrapper className={styles.maskFilters} />
            <MaskTooltip />
            <IOXRayImageSlider />
          </IOXRayReportRender>
        </div>
      </ReportLayout.LeftColumn>
      <ReportLayout.RightColumn data-section="ioxray-report">
        <Report2DToothChart />

        <ToothCardList />

        <Conclusion />

        <ReportActions
          className={styles.reportActions}
          patientID={patientID as string}
          reportID={reportID as string}
        />
      </ReportLayout.RightColumn>

      {/* Modals */}
      <IOXRayChangeTeethNumberModal />
    </>
  );
};
